import React, {
  useEffect,
  useRef,
  useState
} from "react";

import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  RemoveFormatting
} from "lucide-react";


export default function NoteEditor({
  note,
  onChange
}) {

  const [
    saving,
    setSaving
  ] = useState(false);

  const editorRef =
    useRef(null);

  const timer =
    useRef(null);


  // Sync content with editor on note switch
  useEffect(() => {

    if (editorRef.current) {

      // Only update DOM if the content is different to preserve cursor position
      if (editorRef.current.innerHTML !== (note.content || "")) {
        editorRef.current.innerHTML = note.content || "";
      }

    }

  }, [note._id]);


  // Track changes and schedule auto-save
  const handleInput = () => {

    const html =
      editorRef.current.innerHTML;

    setSaving(true);

    clearTimeout(
      timer.current
    );


    timer.current =
      setTimeout(async () => {

        try {

          await onChange({
            content: html
          });

        } finally {

          setSaving(false);

        }

      }, 700);

  };


  // Execute text commands natively in the browser
  const executeCommand = (
    command
  ) => {

    document.execCommand(
      command,
      false,
      null
    );

    // Call input handler to trigger state update and save
    handleInput();

  };


  // Clean up timer on unmount
  useEffect(() => {

    return () => {

      clearTimeout(
        timer.current
      );

    };

  }, []);


  return (

    <div className="editor-wrapper">

      <div className="ql-toolbar ql-snow">

        <span className="ql-formats">

          <button
            type="button"
            className="ql-btn"
            onClick={() =>
              executeCommand("bold")
            }
            title="Bold"
          >
            <Bold size={16} />
          </button>

          <button
            type="button"
            className="ql-btn"
            onClick={() =>
              executeCommand(
                "italic"
              )
            }
            title="Italic"
          >
            <Italic size={16} />
          </button>

          <button
            type="button"
            className="ql-btn"
            onClick={() =>
              executeCommand(
                "underline"
              )
            }
            title="Underline"
          >
            <Underline size={16} />
          </button>

        </span>


        <span className="ql-formats">

          <button
            type="button"
            className="ql-btn"
            onClick={() =>
              executeCommand(
                "insertUnorderedList"
              )
            }
            title="Bullet List"
          >
            <List size={16} />
          </button>

          <button
            type="button"
            className="ql-btn"
            onClick={() =>
              executeCommand(
                "insertOrderedList"
              )
            }
            title="Numbered List"
          >
            <ListOrdered size={16} />
          </button>

        </span>


        <span className="ql-formats">

          <button
            type="button"
            className="ql-btn"
            onClick={() =>
              executeCommand(
                "removeFormat"
              )
            }
            title="Clear Formatting"
          >
            <RemoveFormatting size={16} />
          </button>

        </span>

      </div>


      <div className="editor-container">

        <div
          ref={editorRef}
          className="editable-area"
          contentEditable
          onInput={handleInput}
          data-placeholder="Start writing your note..."
        />

      </div>


      <div className="save-status">

        {saving
          ? "Saving..."
          : "✓ Saved"  
        }

      </div>

    </div>

  );
}